import React, { useState, useEffect } from 'react'
import {
  Scale, FileText, BookOpen, CheckCircle, XCircle,
  AlertTriangle, Loader2, ChevronDown, ChevronUp, Info
} from 'lucide-react'

const API = process.env.REACT_APP_BACKEND_URL

export default function ClientDoctrineTab({ tenantId, policeId }) {
  const [rules, setRules] = useState([])
  const [docs, setDocs] = useState([])
  const [recommandations, setRecommandations] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedDoc, setExpandedDoc] = useState(null)

  useEffect(() => {
    Promise.all([
      fetchDoctrineRules(),
      fetchKBDocs(),
      fetchRecommandations(),
    ]).finally(() => setLoading(false))
  }, [tenantId])

  async function fetchDoctrineRules() {
    try {
      const res = await fetch(`${API}/api/doctrine/regles?tenant_id=${tenantId}`)
      const json = await res.json()
      setRules(json.regles || json.data || [])
    } catch {
      // Will try fallback endpoint
      try {
        const res = await fetch(`${API}/api/doctrine/regles`)
        const json = await res.json()
        setRules(json.regles || json.data || [])
      } catch { /* no rules available */ }
    }
  }

  async function fetchKBDocs() {
    try {
      const res = await fetch(`${API}/api/kb/documents/${tenantId}`)
      const json = await res.json()
      const allDocs = json.documents || []
      // Filter by police if in police context
      if (policeId) {
        setDocs(allDocs.filter(d => d.police_id === policeId))
      } else {
        setDocs(allDocs)
      }
    } catch { /* no docs */ }
  }

  async function fetchRecommandations() {
    try {
      const policeParam = policeId ? `&police_id=${policeId}` : ''
      const res = await fetch(`${API}/api/sinistres?tenant_id=${tenantId}${policeParam}`)
      const json = await res.json()
      const sinistres = json.sinistres || json.data || []
      const recos = []
      for (const s of sinistres.slice(0, 10)) {
        try {
          const r = await fetch(`${API}/api/doctrine/recommandation/${s.id}`)
          const rJson = await r.json()
          if (rJson.recommandation) {
            recos.push({ ...rJson.recommandation, sinistre: s })
          }
        } catch { /* skip */ }
      }
      setRecommandations(recos)
    } catch { /* no recommandations */ }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="doctrine-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  // Categorize docs
  const doctrineClientDocs = docs.filter(d => d.category_code === 'doctrine_client')
  const contractDocs = docs.filter(d => ['conditions_particulieres', 'conditions_generales', 'avenant', 'tableau_garanties', 'tableau_franchises'].includes(d.category_code))
  const otherDocs = docs.filter(d => !['doctrine_client', 'conditions_particulieres', 'conditions_generales', 'avenant', 'tableau_garanties', 'tableau_franchises'].includes(d.category_code))

  return (
    <div className="p-6 space-y-6" data-testid="doctrine-tab">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Scale size={18} className="text-indigo-500" />
        <h2 className="text-base font-semibold text-slate-800">Doctrine client</h2>
      </div>

      {/* Doctrine Rules */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="doctrine-rules-section">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <BookOpen size={14} className="text-indigo-400" />
          <h3 className="text-sm font-semibold text-slate-700">Regles de doctrine appliquees</h3>
          <span className="text-xs text-slate-400 ml-auto">{rules.length} regle(s)</span>
        </div>
        {rules.length === 0 ? (
          <div className="p-8 text-center">
            <Info size={20} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Doctrine C2 standard appliquee par defaut</p>
            <p className="text-xs text-slate-400 mt-1">Aucune regle personnalisee pour ce client</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {rules.map((rule, i) => (
              <div key={rule.id || i} className="px-5 py-3" data-testid={`doctrine-rule-${i}`}>
                <div className="flex items-start gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full mt-0.5 shrink-0 ${
                    rule.recommandation === 'conservation' ? 'bg-emerald-100 text-emerald-700' :
                    rule.recommandation === 'amiable' ? 'bg-blue-100 text-blue-700' :
                    rule.recommandation === 'ouverture_assureur' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {rule.recommandation}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">{rule.nom}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{rule.description}</p>
                    <div className="flex items-center gap-4 mt-1.5 text-[10px] text-slate-400">
                      {rule.seuil_min != null && <span>Min: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(rule.seuil_min)}</span>}
                      {rule.seuil_max != null && <span>Max: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(rule.seuil_max)}</span>}
                      <span>Priorite: {rule.priorite}</span>
                    </div>
                  </div>
                  <span className={`text-xs ${rule.est_active ? 'text-emerald-500' : 'text-slate-400'}`}>
                    {rule.est_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Analyzed Documents */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="doctrine-docs-section">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <FileText size={14} className="text-indigo-400" />
          <h3 className="text-sm font-semibold text-slate-700">Documents contractuels analyses</h3>
          <span className="text-xs text-slate-400 ml-auto">{docs.length} document(s)</span>
        </div>

        {docs.length === 0 ? (
          <div className="p-8 text-center">
            <FileText size={20} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Aucun document analyse</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {/* Doctrine client docs first */}
            {doctrineClientDocs.length > 0 && (
              <div>
                <div className="px-5 py-2 bg-indigo-50/50">
                  <span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Doctrine client</span>
                </div>
                {doctrineClientDocs.map((doc, i) => (
                  <DocRow key={doc.id} doc={doc} expanded={expandedDoc === doc.id} onToggle={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)} testId={`doctrine-doc-${i}`} />
                ))}
              </div>
            )}
            {/* Contract docs */}
            {contractDocs.length > 0 && (
              <div>
                <div className="px-5 py-2 bg-slate-50">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Contrat</span>
                </div>
                {contractDocs.map((doc, i) => (
                  <DocRow key={doc.id} doc={doc} expanded={expandedDoc === doc.id} onToggle={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)} testId={`contract-doc-${i}`} />
                ))}
              </div>
            )}
            {otherDocs.length > 0 && (
              <div>
                <div className="px-5 py-2 bg-slate-50">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Autres</span>
                </div>
                {otherDocs.map((doc, i) => (
                  <DocRow key={doc.id} doc={doc} expanded={expandedDoc === doc.id} onToggle={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)} testId={`other-doc-${i}`} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommandations */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="doctrine-recommandations-section">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-slate-700">Recommandations</h3>
          <span className="text-xs text-slate-400 ml-auto">{recommandations.length} recommandation(s)</span>
        </div>
        {recommandations.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle size={20} className="text-emerald-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Aucune recommandation en attente</p>
            <p className="text-xs text-slate-400 mt-1">Les recommandations apparaissent lors de l'evaluation des sinistres</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {recommandations.map((reco, i) => (
              <div key={reco.id || i} className="px-5 py-3" data-testid={`reco-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    reco.statut === 'validee' ? 'bg-emerald-100 text-emerald-700' :
                    reco.statut === 'ecartee' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {reco.statut || 'en_attente'}
                  </span>
                  <span className="text-xs text-slate-500">
                    {reco.sinistre?.numero_sinistre}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{reco.recommandation_texte || reco.decision}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function DocRow({ doc, expanded, onToggle, testId }) {
  const catLabels = {
    conditions_particulieres: 'Conditions Particulieres',
    conditions_generales: 'Conditions Generales',
    avenant: 'Avenant',
    tableau_garanties: 'Tableau des Garanties',
    tableau_franchises: 'Tableau des Franchises',
    doctrine_client: 'Doctrine Client',
  }

  return (
    <div data-testid={testId}>
      <div
        className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={onToggle}
      >
        <FileText size={14} className="text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700 truncate">{doc.nom_affichage || doc.nom_fichier}</p>
          <p className="text-xs text-slate-400">{catLabels[doc.category_code] || doc.type_document || doc.category_code}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
          doc.extraction_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
          doc.extraction_status === 'error' ? 'bg-red-100 text-red-600' :
          'bg-slate-100 text-slate-500'
        }`}>
          {doc.extraction_status === 'completed' ? 'Analyse' :
           doc.extraction_status === 'error' ? 'Erreur' :
           doc.extraction_status || 'Non analyse'}
        </span>
        {expanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </div>
      {expanded && doc.extraction_status === 'completed' && (
        <div className="px-5 pb-3 pl-12">
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-700 mb-1">Donnees extraites</p>
            {doc.extracted_data ? (
              <pre className="whitespace-pre-wrap text-[11px] font-mono text-slate-500 max-h-48 overflow-auto">
                {typeof doc.extracted_data === 'string'
                  ? doc.extracted_data
                  : JSON.stringify(doc.extracted_data, null, 2)}
              </pre>
            ) : (
              <p className="text-slate-400 italic">Donnees d'extraction non disponibles en detail</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
